/**
 * IOT-HAL-2: VendorAdapter interface contract.
 *
 * Every vendor adapter MUST implement all methods declared here.
 * Use `assertAdapterContract(adapter)` at boot to enforce conformance.
 *
 * The `probe()` method is used at registration time to verify the adapter
 * can communicate with the vendor backend (or operate in offline mode).
 */

/**
 * @typedef {Object} CapabilitySet
 * @property {string[]} sensors         - Sensor types this adapter can report
 * @property {string[]} protocols       - Protocols supported
 * @property {boolean}  otaSupported    - Whether OTA firmware update is available
 * @property {boolean}  fotaSupported   - Whether FOTA (firmware-over-the-air) is distinct from OTA
 * @property {string}   alertingMode    - 'cloud' | 'onboard' | 'hybrid'
 * @property {number}   pushLatencyMs   - Expected config-push latency in milliseconds
 */

/**
 * Canonical vendor adapter interface.
 *
 * All methods are async-compatible. Adapters that communicate synchronously
 * may return resolved promises.
 *
 * Vendors with incomplete integrations MUST use NotImplementedAdapter as their
 * base class — never return null or silently swallow errors.
 */
export class VendorAdapter {
  /**
   * Probe the vendor endpoint / SDK to verify the adapter is functional.
   * Called at boot by halBootstrap after registration.
   * Returns a probe result rather than throwing, so the registry can
   * record degraded adapters without aborting startup.
   *
   * @returns {Promise<{ ok: boolean, latencyMs: number, error?: string }>}
   */
  async probe() {
    throw new Error('VendorAdapter.probe() must be implemented');
  }

  /**
   * Register a device with this vendor's cloud backend.
   *
   * @param {import('./DeviceContract.js').DeviceContractSchema} device
   * @returns {Promise<{ vendorDeviceId: string, registered: boolean }>}
   */
  async register(device) {
    throw new Error('VendorAdapter.register() must be implemented');
  }

  /**
   * Send a command or config delta to a device.
   *
   * @param {string} deviceId
   * @param {Record<string, unknown>} command
   * @returns {Promise<{ acknowledged: boolean, receiptId?: string }>}
   */
  async send(deviceId, command) {
    throw new Error('VendorAdapter.send() must be implemented');
  }

  /**
   * Poll for inbound telemetry / events from the vendor backend.
   * For push-based vendors, return an empty array and use subscribeToTelemetry instead.
   *
   * @returns {Promise<Array<import('./DeviceContract.js').NormalizedReadingSchema>>}
   */
  async receive() {
    throw new Error('VendorAdapter.receive() must be implemented');
  }

  /**
   * Deregister a device from this vendor's backend.
   *
   * @param {string} deviceId
   * @returns {Promise<{ deregistered: boolean }>}
   */
  async deregister(deviceId) {
    throw new Error('VendorAdapter.deregister() must be implemented');
  }

  /**
   * Trigger a firmware-over-the-air update.
   *
   * @param {string} deviceId
   * @param {{ version: string, url?: string, checksum?: string }} firmware
   * @returns {Promise<{ triggered: boolean, jobId?: string }>}
   */
  async fota(deviceId, firmware) {
    throw new Error('VendorAdapter.fota() must be implemented');
  }

  /**
   * Return the static capability set for this adapter.
   * Must be synchronous — called at boot during capability matrix construction.
   *
   * @returns {CapabilitySet}
   */
  capabilities() {
    throw new Error('VendorAdapter.capabilities() must be implemented');
  }
}

// ── Contract enforcement ──────────────────────────────────────────────────────

const REQUIRED_METHODS = ['probe', 'register', 'send', 'receive', 'deregister', 'fota', 'capabilities'];

/**
 * Assert that an adapter object implements the full VendorAdapter contract.
 * Throws a descriptive error listing any missing methods.
 * Called by halBootstrap before `HalService.registerAdapter()`.
 *
 * @param {object} adapter
 * @param {string} vendorId  - For error messages
 */
export function assertAdapterContract(adapter, vendorId) {
  if (!adapter || typeof adapter !== 'object') {
    throw new Error(`[halBootstrap] adapter for "${vendorId}" is not an object`);
  }
  const missing = REQUIRED_METHODS.filter(m => typeof adapter[m] !== 'function');
  if (missing.length > 0) {
    throw new Error(
      `[halBootstrap] adapter for "${vendorId}" is missing required methods: ${missing.join(', ')}`
    );
  }
}
